import { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import LinkExtension from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Briefcase, Home, ChevronLeft, Save, Eye, Trash2,
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered,
  Heading2, Heading3, Quote, Link as LinkIcon, Undo, Redo
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';

const categories = [
  { id: 'consejos', label: 'Consejos' },
  { id: 'guias', label: 'Guías' },
  { id: 'noticias', label: 'Noticias' },
  { id: 'testimonios', label: 'Testimonios' }
];

const MenuBar = ({ editor }) => {
  if (!editor) return null;

  const setLink = useCallback(() => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL del enlace:', previousUrl);
    
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const btnClass = (isActive) => `p-2 rounded transition-colors ${
    isActive ? 'bg-slate-200 text-slate-900' : 'text-slate-600 hover:bg-slate-100'
  }`;

  return (
    <div className="flex flex-wrap gap-1 p-3 border-b border-slate-200 bg-slate-50 rounded-t-lg">
      <button
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={btnClass(editor.isActive('bold'))}
        title="Negrita"
      >
        <Bold className="w-4 h-4" />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={btnClass(editor.isActive('italic'))}
        title="Cursiva"
      >
        <Italic className="w-4 h-4" />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={btnClass(editor.isActive('underline'))}
        title="Subrayado"
      >
        <UnderlineIcon className="w-4 h-4" />
      </button>
      
      <div className="w-px h-6 bg-slate-300 mx-1 self-center" />
      
      <button
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={btnClass(editor.isActive('heading', { level: 2 }))}
        title="Título grande"
      >
        <Heading2 className="w-4 h-4" />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={btnClass(editor.isActive('heading', { level: 3 }))}
        title="Subtítulo"
      >
        <Heading3 className="w-4 h-4" />
      </button>
      
      <div className="w-px h-6 bg-slate-300 mx-1 self-center" />
      
      <button
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={btnClass(editor.isActive('bulletList'))}
        title="Lista con viñetas"
      >
        <List className="w-4 h-4" />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={btnClass(editor.isActive('orderedList'))}
        title="Lista numerada"
      >
        <ListOrdered className="w-4 h-4" />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className={btnClass(editor.isActive('blockquote'))}
        title="Cita"
      >
        <Quote className="w-4 h-4" />
      </button>
      
      <div className="w-px h-6 bg-slate-300 mx-1 self-center" />
      
      <button
        onClick={setLink}
        className={btnClass(editor.isActive('link'))}
        title="Enlace"
      >
        <LinkIcon className="w-4 h-4" />
      </button>
      
      <div className="w-px h-6 bg-slate-300 mx-1 self-center" />
      
      <button
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        className="p-2 rounded text-slate-600 hover:bg-slate-100 disabled:opacity-30"
        title="Deshacer"
      >
        <Undo className="w-4 h-4" />
      </button>
      <button
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        className="p-2 rounded text-slate-600 hover:bg-slate-100 disabled:opacity-30"
        title="Rehacer"
      >
        <Redo className="w-4 h-4" />
      </button>
    </div>
  );
};

const BlogEditorPage = ({ user }) => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(postId);
  
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    category: 'consejos',
    cover_image: '',
    is_published: true
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      LinkExtension.configure({
        openOnClick: false,
      }),
      Placeholder.configure({
        placeholder: 'Escribe tu artículo aquí... Usa el menú de arriba para dar formato.',
      }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'prose prose-lg max-w-none min-h-[400px] p-4 focus:outline-none',
      },
    },
  });

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      toast.error('Acceso denegado');
      navigate('/blog');
      return;
    }

    if (isEditing) {
      fetchPost();
    }
  }, [postId, user]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/blog/id/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const post = await response.json();
        setFormData({
          title: post.title,
          excerpt: post.excerpt,
          category: post.category,
          cover_image: post.cover_image || '',
          is_published: post.is_published
        });
        if (editor) {
          editor.commands.setContent(post.content);
        }
      } else {
        toast.error('No se pudo cargar el artículo');
        navigate('/blog');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar el artículo');
    } finally {
      setLoading(false);
    }
  };

  // Update editor content when it's ready and we're editing
  useEffect(() => {
    if (editor && isEditing && !loading) {
      fetchPost();
    }
  }, [editor]);

  const handleSave = async (publish = true) => {
    if (!formData.title.trim()) {
      toast.error('El título es obligatorio');
      return;
    }
    if (!editor?.getHTML() || editor.getHTML() === '<p></p>') {
      toast.error('El contenido es obligatorio');
      return;
    }
    if (!formData.excerpt.trim()) {
      toast.error('El extracto es obligatorio');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const body = {
        ...formData,
        content: editor.getHTML(),
        is_published: publish
      };

      const url = isEditing ? `${API}/blog/${postId}` : `${API}/blog`;
      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const savedPost = await response.json();
        toast.success(isEditing ? 'Artículo actualizado' : 'Artículo creado');
        navigate(`/blog/${savedPost.slug}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al guardar');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al guardar el artículo');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('¿Estás seguro de eliminar este artículo?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/blog/${postId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Artículo eliminado');
        navigate('/blog');
      } else {
        toast.error('Error al eliminar');
      }
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white z-50 border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <Link to="/" className="flex items-center space-x-2">
              <Briefcase className="w-6 h-6 sm:w-8 sm:h-8 text-red-600" />
              <span className="text-lg sm:text-2xl font-bold text-slate-900">JobSuiza</span>
            </Link>

            <div className="flex items-center space-x-2 sm:space-x-4">
              <Link to="/blog">
                <Button variant="ghost" size="sm">
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Blog
                </Button>
              </Link>
              
              {isEditing && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleDelete}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Eliminar</span>
                </Button>
              )}
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => handleSave(false)}
                disabled={saving}
              >
                <Eye className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Borrador</span>
              </Button>
              
              <Button 
                size="sm" 
                onClick={() => handleSave(true)}
                disabled={saving}
                className="bg-red-600 hover:bg-red-700"
              >
                <Save className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">{saving ? 'Guardando...' : 'Publicar'}</span>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Editor */}
      <div className="pt-20 pb-12 px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-2xl font-bold text-slate-900 mb-6">
            {isEditing ? 'Editar Artículo' : 'Nuevo Artículo'}
          </h1>

          <div className="space-y-6">
            {/* Title */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <Label htmlFor="title" className="text-base font-semibold">Título</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Escribe un título atractivo..."
                className="mt-2 text-lg"
                data-testid="post-title"
              />
            </div>

            {/* Category and Image */}
            <div className="bg-white rounded-lg shadow-sm p-6 grid sm:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="category" className="text-base font-semibold">Categoría</Label>
                <select
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  data-testid="post-category"
                >
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="cover_image" className="text-base font-semibold">Imagen de Portada (URL)</Label>
                <Input
                  id="cover_image"
                  value={formData.cover_image}
                  onChange={(e) => setFormData({ ...formData, cover_image: e.target.value })}
                  placeholder="https://..."
                  className="mt-2"
                  data-testid="post-cover"
                />
              </div>
            </div>

            {/* Excerpt */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <Label htmlFor="excerpt" className="text-base font-semibold">Extracto</Label>
              <p className="text-sm text-slate-500 mb-2">
                Un breve resumen que aparecerá en la lista del blog (2-3 oraciones)
              </p>
              <Textarea
                id="excerpt"
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                placeholder="Escribe un resumen breve del artículo..."
                rows={3}
                className="mt-1"
                data-testid="post-excerpt"
              />
            </div>

            {/* Content Editor */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <Label className="text-base font-semibold block p-6 pb-3">Contenido</Label>
              <div className="border border-slate-200 mx-6 mb-6 rounded-lg overflow-hidden">
                <MenuBar editor={editor} />
                <EditorContent editor={editor} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogEditorPage;
